import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import { Grid } from '@/components/common/grid';
import { Input } from 'src/components/ui/input';
import { Spinner } from 'src/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Badge } from 'src/components/ui/badge';
import { Button as IconButton } from 'src/components/ui/button';
import { LocationPicker } from '../LocationPicker';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import CustomerPill from 'src/components/customer/CustomerPill';
import LocationPill from 'src/components/location/LocationPill';
import {
  WarningIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import useSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useSalesOrderDraft';
import useUpdateSalesOrderDraftField from 'src/api/accounts-receivable/sales-order-drafts/useUpdateSalesOrderDraftField';
import useUpdateSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useUpdateSalesOrderDraftLineItem';
import useCreateSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useCreateSalesOrderDraftLineItem';
import useDeleteSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useDeleteSalesOrderDraftLineItem';
import useApproveSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useApproveSalesOrderDraft';
import useRejectSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useRejectSalesOrderDraft';
import useDeleteSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useDeleteSalesOrderDraft';
import useUpdatePurchaseOrderCustomer from 'src/api/accounts-receivable/useUpdatePurchaseOrderCustomer';
import useMatchLineItemToProductVariant from 'src/api/inventory-management/products/useMatchLineItemToProductVariant';
import {
  useAddSalesOrderDraftCharge,
  useUpdateSalesOrderDraftCharge,
  useDeleteSalesOrderDraftCharge,
} from 'src/api/accounts-receivable/sales-order-drafts/useManageSalesOrderDraftCharges';
import AssociateLineItemModal from '../AssociateLineItemModal';
import LineItemsTable from '../line-item-table/LineItemsTable';
import { TranslationRefreshBanner } from './TranslationRefreshBanner';
import { ErpSyncErrorBanner } from './ErpSyncErrorBanner';
import DeleteLineItemModal from '../DeleteLineItemModal';
import RejectSalesOrderDraftModal from '../RejectSalesOrderDraftModal';
import { PendingChangesPanel } from './PendingChangesPanel';
import type {
  PendingLineItemChange,
  PendingHeaderChange,
} from 'src/types/application/SalesOrderDraftDTO';
import ChargesSection from '../ChargesSection';
import SourceBadge from 'src/components/source-badge/SourceBadge';
import {
  SalesOrderSource,
  SalesOrderDraftStatus,
  LineItem,
  Charge,
} from 'src/types/application/SalesOrderDraftDTO';
import { SalesOrderDraftStatusBadge } from 'src/components/sales-order-draft-status-badge/SalesOrderDraftStatusBadge';
import { Watchers } from 'src/components/watchers/Watchers';
import { EmailData } from '@/components/email-viewer/email-viewer-types';
import { T, Var, useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { useIsOneToOneProductMapping } from '@/hooks/useIsOneToOneProductMapping';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { cn } from '@/lib/utils';
import { MemoryBankNotes } from '@/components/memory-bank/MemoryBankNotes';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { InfoHint } from '@/components/common/InfoHint';
import {
  //  AIWorkflow,
  SystemEntity,
} from '@erp/shared-types';
import { ReextractSalesOrderDraftDialog } from '@/components/purchase-orders/ReextractSalesOrderDraftDialog';
import { RobotRetryButton } from '@/components/common/robot-retry-button';
import { useQueryClient } from '@tanstack/react-query';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { SalesOrderSyncStatus } from '@/types/application/SalesOrderDTOV2_DEPRECATED';
import OptionsMenu from './OptionsMenu';
import useRefreshEdiTransaction from 'src/api/accounts-receivable/sales-order-drafts/useRefreshEdiTransaction';
import {
  canDeleteProcessedDraft,
  getSourceFlags,
  extractEmailDomain,
  formatDateOnly,
  parseDateOnly,
  isDatePast,
  isDraftLineMatched,
} from './utils';
import type { SalesOrderDraftModalProps, ValidationError } from './types';
import { isDraftModalBusy } from './isDraftModalBusy';
import { SyncErrorDialog } from './SyncErrorDialog';
import { SourceDocumentViewer } from './SourceDocumentViewer';
import { ActivityGraph } from '@/components/activity-graph';

const showChargeModal = false;
const showReextractButton = false;
const EMPTY_PENDING_LINE_ITEMS: PendingLineItemChange[] = [];
const EMPTY_PENDING_HEADERS: PendingHeaderChange[] = [];

const SalesOrderDraftModal: React.FC<SalesOrderDraftModalProps> = ({
  salesOrderDraftId,
  onUpdate,
  onClose,
  editable,
  navigationInfo,
  isLoading: _isLoading,
  onSubModalStateChange,
  onSalesOrderCreated,
}) => {
  const navigate = useNavigate({ from: '/sales-orders-v2' });
  const queryClient = useQueryClient();
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();

  const { enabled: isActivityLogEnabled } = useFeatureFlag(
    'activity-log-enabled',
    false,
  );

  const {
    data: salesOrderDraftData,
    isLoading: _isSalesOrderDraftLoading,
    isError: isSalesOrderDraftError,
    refetch: refetchSalesOrderDraft,
  } = useSalesOrderDraft(salesOrderDraftId);

  const salesOrderDraft = salesOrderDraftData?.stagedPurchaseOrder;

  const customerId =
    salesOrderDraft?.customerId || salesOrderDraft?.suggestedCustomerId;

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _customerName =
    salesOrderDraft?.customer?.erpDisplayName ||
    salesOrderDraft?.customer?.name;

  const sourceFlags = getSourceFlags(salesOrderDraft?.source);
  const { isEdiSource, isEmailSource } = sourceFlags;

  // Check if sales order draft is already processed (approved or rejected)
  const isProcessed =
    salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED ||
    salesOrderDraft?.status === SalesOrderDraftStatus.REJECTED;

  // Pending 860 changes: show review panel whenever there are actual pending changes
  const pendingLineItemChanges =
    salesOrderDraft?.pendingLineItemChanges ?? EMPTY_PENDING_LINE_ITEMS;
  const pendingHeaderChanges =
    salesOrderDraft?.pendingHeaderChanges ?? EMPTY_PENDING_HEADERS;
  const hasPendingChanges =
    pendingLineItemChanges.length + pendingHeaderChanges.length > 0;

  // Stable key to remount PendingChangesPanel (resetting its internal state)
  // when the pending changes payload actually changes
  const pendingChangesKey = useMemo(
    () =>
      JSON.stringify(salesOrderDraft?.pendingLineItemChanges) +
      JSON.stringify(salesOrderDraft?.pendingHeaderChanges),
    [
      salesOrderDraft?.pendingLineItemChanges,
      salesOrderDraft?.pendingHeaderChanges,
    ],
  );

  const hasLoadedDraft = !!salesOrderDraft && !isSalesOrderDraftError;

  // Modal should only be editable if editable prop is true AND the PO isn't already processed
  const isModalEditable = hasLoadedDraft && editable && !isProcessed;

  // Determine if actions should be shown (only show for non-processed and editable POs)
  const showActions = hasLoadedDraft && editable && !isProcessed;

  const canDeleteProcessedEdiDraft = canDeleteProcessedDraft({
    hasLoadedDraft,
    isProcessed,
    isEdiSource,
    isAdminView: isAdmin(currentUser, viewMode),
  });

  const customerEmailDomain = useMemo(
    () =>
      salesOrderDraft?.customer?.emailDomain ||
      salesOrderDraft?.suggestedCustomer?.emailDomain ||
      extractEmailDomain(salesOrderDraft?.sourceEmail?.from),
    [
      salesOrderDraft?.customer?.emailDomain,
      salesOrderDraft?.suggestedCustomer?.emailDomain,
      salesOrderDraft?.sourceEmail?.from,
    ],
  );

  // State for right panel visibility - default based on source
  const [isRightPanelVisible, setIsRightPanelVisible] = useState<boolean>(
    () => true, // Always show right panel by default regardless of source
  );

  // State for error dialog
  const [syncErrorDialogOpen, setSyncErrorDialogOpen] = useState(false);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string | null>(null);
  const [erpSystemName, setErpSystemName] = useState<string | null>(null);

  // Show translation refresh banner after approval fails with customer product matching error

  const approveStagedPurchaseOrder = useApproveSalesOrderDraft(
    salesOrderDraftId,
    ({ salesOrderId, originSyncStatus }) => {
      // Notify parent about the created sales order
      onSalesOrderCreated?.(salesOrderId);

      // Close the draft modal
      onClose(true);

      // If sync failed, navigate to the sales order page
      if (originSyncStatus !== SalesOrderSyncStatus.success) {
        navigate({
          to: '/sales-orders-v2/$salesOrderId',
          params: { salesOrderId },
        });
      }
    },
    (error) => {
      const errorData = error as {
        message?: string;
        erpSystemName?: string;
        erpErrorDetails?: string;
      };

      if (errorData.erpSystemName) {
        const displayMessage =
          errorData.erpErrorDetails || errorData.message || String(error);
        setErpSystemName(errorData.erpSystemName);
        setSyncErrorMessage(displayMessage);
        setSyncErrorDialogOpen(true);
      }
    },
  );

  const rejectStagedPurchaseOrder = useRejectSalesOrderDraft(
    salesOrderDraftId,
    () => {
      onClose(true);
    },
  );

  const deleteStagedPurchaseOrder = useDeleteSalesOrderDraft(
    salesOrderDraftId,
    () => {
      onClose(true);
    },
  );

  const updateStagedPurchaseOrderLineItem = useUpdateSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      // On success: clear optimistic updates and pending updates, then refetch
      setOptimisticUpdates({});
      setPendingUpdates({});
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const createLineItem = useCreateSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const deleteLineItem = useDeleteSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  // Charge management hooks
  const addOrderCharge = useAddSalesOrderDraftCharge(salesOrderDraftId, () => {
    onUpdate?.();
    refetchSalesOrderDraft();
  });

  const updateOrderCharge = useUpdateSalesOrderDraftCharge(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const deleteOrderCharge = useDeleteSalesOrderDraftCharge(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const matchLineItemToProductVariant = useMatchLineItemToProductVariant(() => {
    onUpdate?.();
    refetchSalesOrderDraft();
  });

  const updatePurchaseOrderField = useUpdateSalesOrderDraftField(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const updateCustomer = useUpdatePurchaseOrderCustomer(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
      // Invalidate watchers query since changing customer updates assignees
      queryClient.invalidateQueries({
        queryKey: [`core/watches/staged_purchase_order/${salesOrderDraftId}`],
      });
    },
  );

  const handleCustomerSelect = useCallback(
    (customerId: string | undefined) => {
      if (customerId) {
        updateCustomer.mutateAsync({ customerId });
      }
    },
    [updateCustomer],
  );

  // TODO
  // const addLineItemCharge = useAddStagedPurchaseOrderLineItemCharge(
  //   salesOrderDraftId,
  //   () => {
  //     onUpdate?.();
  //     refetchSalesOrderDraft();
  //   },
  // );

  // const updateLineItemCharge = useUpdateSalesOrderDraftLineItemCharge(
  //   salesOrderDraftId,
  //   () => {
  //     onUpdate?.();
  //     refetchSalesOrderDraft();
  //   },
  // );

  // const deleteLineItemCharge = useDeleteSalesOrderDraftLineItemCharge(
  //   salesOrderDraftId,
  //   () => {
  //     onUpdate?.();
  //     refetchSalesOrderDraft();
  //   },
  // );

  // Convert InboundEmailDTO to EmailData with proper typing
  const emailData: EmailData = salesOrderDraft?.sourceData?.email
    ? {
        ...inboundEmailToEmailData(salesOrderDraft.sourceData.email),
        documentAssociations:
          salesOrderDraft?.sourceEmail?.documentAssociations,
      }
    : {
        inboundEmailId: undefined,
        subject: undefined,
        from: undefined,
        to: undefined,
        htmlContent: undefined,
        textContent: undefined,
        attachments: undefined,
        documentAssociations:
          salesOrderDraft?.sourceEmail?.documentAssociations,
      };

  const ediData = {
    ordefulMessage: salesOrderDraft?.sourceData?.orderfulJson,
  };

  const documentData = {
    document: salesOrderDraft?.sourceData?.document,
  };

  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState<number>();
  const [selectedLineItemSnapshot, setSelectedLineItemSnapshot] =
    useState<LineItem>();
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [showShipDateModal, setShowShipDateModal] = useState(false);
  // Inline editing state for PO number
  const [isEditingPurchaseOrderNumber, setIsEditingPurchaseOrderNumber] =
    useState(false);
  const [purchaseOrderNumberInput, setPurchaseOrderNumberInput] = useState('');
  const [showDeleteLineItemModal, setShowDeleteLineItemModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReextractDialog, setShowReextractDialog] = useState(false);
  const [deletingLineItem, setDeletingLineItem] = useState<
    LineItem | undefined
  >();
  const [deletingLineItemIndex, setDeletingLineItemIndex] = useState<
    number | undefined
  >();
  const { showToast } = useToast();

  const refreshEdiTransaction = useRefreshEdiTransaction({
    salesOrderDraftId,
    onSuccess: () => {
      refetchSalesOrderDraft();
    },
    onError: () => {
      showToast({
        message: 'Failed to refresh EDI transaction',
        color: 'error',
        icon: 'error',
      });
    },
  });

  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationError>({});

  // Optimistic updates tracking
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<number, Partial<LineItem>>
  >({});

  // Track which fields are currently being updated (for visual feedback)
  const [pendingUpdates, setPendingUpdates] = useState<
    Record<
      number,
      Set<
        | 'quantity'
        | 'unitPrice'
        | 'rescheduledDate'
        | 'customerDescription'
        | 'customerIdentifier'
      >
    >
  >({});

  const lineItems: LineItem[] = useMemo(
    () => salesOrderDraft?.lineItems || [],
    [salesOrderDraft?.lineItems],
  );

  // Apply optimistic updates to line items for display
  const displayLineItems = useMemo(
    () =>
      lineItems.map((item, index) => ({
        ...item,
        ...optimisticUpdates[index],
      })),
    [lineItems, optimisticUpdates],
  );

  // Lines with no product. EDI matching sets `selectedTranslationListItemId`
  // (the Famous translation-list entry, which is the whole product reference for
  // EDI); email / file-upload lines carry `selectedCustomerProductId` or a
  // directly associated variant. Any one of the three counts as matched — an EDI
  // line whose Famous product hasn't been ingested into our catalog has no
  // variant and is still perfectly orderable.
  const unmatchedLineItemCount = useMemo(
    () => displayLineItems.filter((item) => !isDraftLineMatched(item)).length,
    [displayLineItems],
  );

  const isOneToOneProduct = useIsOneToOneProductMapping();

  const handleAssociateVariantSelected = useCallback(
    (rowIndex: number, item: LineItem, productVariantId?: string) => {
      if (!productVariantId || !customerId) return;
      matchLineItemToProductVariant.mutate({
        stagedCustomerPurchaseOrderId: salesOrderDraftId,
        stagedCustomerPurchaseOrderLineItemIndex: rowIndex,
        customerId,
        productVariantId,
        customerDescription:
          item.customerDescription || item.itemName || item.name,
        customerMainIdentifier: item.customerIdentifier || item.sku || item.upc,
        allowCreateCustomerProduct: isOneToOneProduct || undefined,
      });
    },
    [
      customerId,
      matchLineItemToProductVariant,
      salesOrderDraftId,
      isOneToOneProduct,
    ],
  );

  // Use snapshot for modal display to prevent infinite re-renders
  const selectedLineItem = selectedLineItemSnapshot;

  // CRUD handlers for line items
  const handleAddLineItem = () => {
    window.alert('TODO: Add line item');
  };

  const handleUpdateLineItem = (
    index: number,
    field:
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier',
    value: number | string,
  ) => {
    const lineItem = lineItems[index];
    if (!lineItem) return;

    // Apply optimistic update immediately
    setOptimisticUpdates((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value,
      },
    }));

    // Mark field as pending
    setPendingUpdates((prev) => ({
      ...prev,
      [index]: new Set([...(prev[index] || []), field]),
    }));

    const updatedLineItem = {
      ...lineItem,
      [field]: value,
    };

    updateStagedPurchaseOrderLineItem.mutate(
      {
        index,
        before: lineItem,
        after: updatedLineItem,
      },
      {
        onError: () => {
          // On error: revert the optimistic update and clear pending state
          setOptimisticUpdates((prev) => {
            const newUpdates = { ...prev };
            if (newUpdates[index]) {
              delete newUpdates[index][field as keyof LineItem];
              if (Object.keys(newUpdates[index]).length === 0) {
                delete newUpdates[index];
              }
            }
            return newUpdates;
          });

          setPendingUpdates((prev) => {
            const newPending = { ...prev };
            if (newPending[index]) {
              newPending[index].delete(field);
              if (newPending[index].size === 0) {
                delete newPending[index];
              }
            }
            return newPending;
          });
        },
      },
    );
  };

  const handleDeleteLineItem = (index: number) => {
    const lineItem = lineItems[index];
    setDeletingLineItem(lineItem);
    setDeletingLineItemIndex(index);
    setShowDeleteLineItemModal(true);
  };

  const handleToggleRejectLineItem = (index: number) => {
    const lineItem = lineItems[index];
    if (!lineItem) return;

    const updatedLineItem = {
      ...lineItem,
      ediRejected: !lineItem.ediRejected,
    };

    updateStagedPurchaseOrderLineItem.mutate({
      index,
      before: lineItem,
      after: updatedLineItem,
    });
  };

  const handleConfirmDelete = () => {
    if (deletingLineItemIndex !== undefined) {
      deleteLineItem.mutate({ index: deletingLineItemIndex });
      setShowDeleteLineItemModal(false);
      setDeletingLineItem(undefined);
      setDeletingLineItemIndex(undefined);
    }
  };

  // Feature flag to switch to AssociateProductV2 flow
  const { enabled: isAssociateProductV2Enabled } = useFeatureFlag(
    'associate-product-v2-enabled',
    false,
  );

  const shouldShowAssociateModal =
    selectedLineItemIndex !== undefined && !!selectedLineItem && !!customerId;

  const handleVariantSelected = useCallback(
    ({ productVariantId }: { productVariantId?: string }) => {
      if (productVariantId) {
        if (
          selectedLineItemIndex !== undefined &&
          customerId &&
          selectedLineItem
        ) {
          matchLineItemToProductVariant.mutate({
            stagedCustomerPurchaseOrderId: salesOrderDraftId,
            stagedCustomerPurchaseOrderLineItemIndex: selectedLineItemIndex,
            customerId,
            productVariantId,
            customerDescription:
              selectedLineItem.customerDescription ||
              selectedLineItem.itemName ||
              selectedLineItem.name,
            customerMainIdentifier:
              selectedLineItem.customerIdentifier ||
              selectedLineItem.sku ||
              selectedLineItem.upc,
            allowCreateCustomerProduct: isOneToOneProduct || undefined,
          });
        } else {
          if (!customerId) {
            window.alert(
              'Customer ID is required to associate a line item to a product variant.',
            );
          } else {
            window.alert(
              'Something went wrong. Please contact the Gently team.',
            );
          }
        }
      }
      // Clear selection and snapshot
      setSelectedLineItemIndex(undefined);
      setSelectedLineItemSnapshot(undefined);
    },
    [
      selectedLineItemIndex,
      customerId,
      selectedLineItem,
      matchLineItemToProductVariant,
      salesOrderDraftId,
      isOneToOneProduct,
    ],
  );

  const renderAssociateModal = useCallback(() => {
    if (!shouldShowAssociateModal) return null;

    if (isAssociateProductV2Enabled) {
      // When the new flow is enabled, the popover trigger is rendered inline in the table cell.
      return null;
    }

    return (
      <AssociateLineItemModal
        lineItem={selectedLineItem}
        customerId={customerId}
        editable={isModalEditable}
        onClose={(productVariantId) => {
          handleVariantSelected({ productVariantId });
        }}
      />
    );
  }, [
    shouldShowAssociateModal,
    isAssociateProductV2Enabled,
    customerId,
    selectedLineItem,
    isModalEditable,
    handleVariantSelected,
  ]);

  // Charge handlers
  const handleAddOrderCharge = (charge: Charge) => {
    addOrderCharge.mutate({ charge });
  };

  const handleUpdateOrderCharge = (charge: Charge) => {
    if (charge.id) {
      updateOrderCharge.mutate({ charge, chargeId: charge.id });
    }
  };

  const handleDeleteOrderCharge = (chargeId: string) => {
    deleteOrderCharge.mutate({ chargeId });
  };

  const handleInlineShipDateUpdate = (next: Date | undefined) => {
    if (!next) return;
    const parsed = parseDateOnly(next);
    if (!parsed) return;
    const nextDateOnly = parsed;
    const delivery = parseDateOnly(salesOrderDraft?.deliveryDate);
    if (delivery && nextDateOnly > delivery) {
      showToast({
        title: 'Invalid date',
        message: 'Ship date must be on or before the delivery date.',
        color: 'error',
        icon: 'warning',
      });
      return;
    }
    updateField.mutate({
      field: 'shipDate',
      value: formatDateOnly(nextDateOnly),
    });
  };

  const handleInlineDeliveryDateUpdate = (next: Date | undefined) => {
    if (!next) return;
    const parsed = parseDateOnly(next);
    if (!parsed) return;
    const nextDate = parsed;
    const ship = parseDateOnly(salesOrderDraft?.shipDate);
    if (ship && nextDate < ship) {
      showToast({
        title: 'Invalid date',
        message: 'Delivery date must be on or after the ship date.',
        color: 'error',
        icon: 'warning',
      });
      return;
    }
    updateField.mutate({
      field: 'deliveryDate',
      value: formatDateOnly(nextDate),
    });
  };

  const updateField = useUpdateSalesOrderDraftField(salesOrderDraftId, () => {
    onUpdate?.();
    refetchSalesOrderDraft();
    setShowDeliveryDateModal(false);
    setShowShipDateModal(false);
    setIsEditingPurchaseOrderNumber(false);
  });

  const isLoading = isDraftModalBusy({
    pageLoading: _isLoading,
    draftLoading: _isSalesOrderDraftLoading,
    approveLoading: approveStagedPurchaseOrder.isLoading,
    rejectLoading: rejectStagedPurchaseOrder.isLoading,
    deleteLoading: deleteStagedPurchaseOrder.isLoading,
    lineItemUpdateLoading: updateStagedPurchaseOrderLineItem.isLoading,
    lineItemCreateLoading: createLineItem.isLoading,
    lineItemDeleteLoading: deleteLineItem.isLoading,
    matchLoading: matchLineItemToProductVariant.isLoading,
    customerUpdateLoading: updateCustomer.isLoading,
  });

  // Notify parent when any sub-modals are open
  useEffect(() => {
    const isAnySubModalOpen =
      selectedLineItemIndex !== undefined ||
      showDeliveryDateModal ||
      showShipDateModal ||
      showDeleteLineItemModal ||
      showRejectModal ||
      showDeleteModal ||
      showReextractDialog;
    onSubModalStateChange?.(isAnySubModalOpen);
  }, [
    selectedLineItemIndex,
    showDeliveryDateModal,
    showShipDateModal,
    showDeleteLineItemModal,
    showRejectModal,
    showDeleteModal,
    showReextractDialog,
    onSubModalStateChange,
  ]);

  // Update right panel visibility when source changes
  useEffect(() => {
    setIsRightPanelVisible(true); // Always show right panel for all sources
  }, [isEdiSource]);

  // Clear optimistic updates when sales order draft data changes (e.g., from refetch)
  useEffect(() => {
    setOptimisticUpdates({});
  }, [salesOrderDraft?.lineItems]);

  // Set initial PO number input value when modal opens
  useEffect(() => {
    if (isEditingPurchaseOrderNumber) {
      setPurchaseOrderNumberInput(salesOrderDraft?.purchaseOrderNumber || '');
    }
  }, [isEditingPurchaseOrderNumber, salesOrderDraft?.purchaseOrderNumber]);

  // Clear validation errors and hook errors when sales order draft changes
  // useEffect(() => {
  //   setValidationErrors({});

  //   // Reset all mutation hook errors
  //   approveStagedPurchaseOrder.reset();
  //   rejectStagedPurchaseOrder.reset();
  //   updateStagedPurchaseOrderLineItem.reset();
  //   createLineItem.reset();
  //   deleteLineItem.reset();
  //   matchLineItemToProductVariant.reset();
  //   updatePurchaseOrderField.reset();
  //   updateField.reset();
  // }, [salesOrderDraftId]);

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleConfirmReject = () => {
    rejectStagedPurchaseOrder.mutate();
    setShowRejectModal(false);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteDraft = () => {
    deleteStagedPurchaseOrder.mutate();
    setShowDeleteModal(false);
  };

  const handleToggleRightPanel = () => {
    setIsRightPanelVisible(!isRightPanelVisible);
  };

  // Validation logic
  const validateForm = () => {
    const errors: ValidationError = {};

    console.log('Validating form:', {
      isEmailSource,
      shipToLocationId: salesOrderDraft?.shipToLocationId,
      billToLocationId: salesOrderDraft?.billToLocationId,
      buyingPartyLocationId: salesOrderDraft?.buyingPartyLocationId,
      source: salesOrderDraft?.source,
    });

    // Check if shipping location is required (for all sources)
    if (!salesOrderDraft?.shipToLocationId) {
      errors.shippingLocation = 'Shipping location is required';
      console.log('Validation failed: missing shipping location');
    }

    setValidationErrors(errors);
    console.log('Validation errors:', errors);
    return Object.keys(errors).length === 0;
  };

  // Handle shipping location selection
  const handleLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'shipToLocationId',
        value: locationId,
      });
      // Clear validation error when location is selected
      setValidationErrors((prev) => ({ ...prev, shippingLocation: undefined }));
    }
  };

  // Handle approve with validation
  const handleApprove = () => {
    console.log('handleApprove called');
    const isValid = validateForm();
    console.log('Form is valid:', isValid);
    if (isValid) {
      console.log('Proceeding with approval');
      approveStagedPurchaseOrder.mutate({});
    } else {
      console.log('Form validation failed, not proceeding with approval');
    }
  };

  return (
    <Modal
      maxWidth="xl"
      dialogSx={hasPendingChanges ? { maxWidth: '1600px' } : undefined}
      fullScreenable
      disableContentOverflow
      open={true}
      optionsMenu={
        <OptionsMenu
          purchaseOrderNumber={salesOrderDraft?.purchaseOrderNumber}
        />
      }
      title={
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDTypography variant="h6">
              {salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED ? (
                <T context="modal title for approved sales order">
                  Sales Order
                </T>
              ) : (
                <T context="modal title for pending sales order draft">
                  Sales Order Draft
                </T>
              )}
            </MDTypography>
            {salesOrderDraft?.status && (
              <SalesOrderDraftStatusBadge status={salesOrderDraft.status} />
            )}
            <Watchers
              watchableEntityType="staged_purchase_order"
              watchableId={salesOrderDraftId}
            />
            {isEdiSource && isAdmin(currentUser, viewMode) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      variant="ghost"
                      size="icon"
                      aria-label={gt('Refresh from Orderful', {
                        context: 'aria label for the refresh EDI button',
                      })}
                      disabled={refreshEdiTransaction.isLoading}
                      onClick={() => refreshEdiTransaction.mutate()}
                    >
                      <ArrowsClockwiseIcon
                        className={
                          refreshEdiTransaction.isLoading ? 'animate-spin' : ''
                        }
                        size={18}
                      />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <T>Refresh from Orderful</T>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </MDBox>
          {navigationInfo && (
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox
                onClick={() => navigationInfo.onNavigate('prev')}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderRadius: '50%',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <CaretLeftIcon size={20} />
              </MDBox>
              <MDTypography variant="body2">
                <T context="navigation counter, e.g. 1 of 5">
                  <Var>{navigationInfo.currentIndex + 1}</Var> of{' '}
                  <Var>{navigationInfo.totalItems}</Var>
                </T>
              </MDTypography>
              <MDBox
                onClick={() => navigationInfo.onNavigate('next')}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderRadius: '50%',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <CaretRightIcon size={20} />
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      }
      closeable
      isLoading={isLoading}
      error={
        approveStagedPurchaseOrder.error ||
        rejectStagedPurchaseOrder.error ||
        deleteStagedPurchaseOrder.error ||
        updateStagedPurchaseOrderLineItem.error ||
        matchLineItemToProductVariant.error
      }
      onCloseButton={{
        text: 'Close',
        onClick: onClose,
        disabled: approveStagedPurchaseOrder.isLoading,
      }}
      hideCloseButton
      onConfirmButton={
        showActions && !hasPendingChanges
          ? {
              text: 'Approve and Create Sales Order',
              disabled: isLoading,
              onClick: handleApprove,
            }
          : undefined
      }
      additionalButtons={
        showActions && !hasPendingChanges
          ? [
              ...(isAdmin(currentUser, viewMode)
                ? [
                    {
                      text: 'Delete',
                      variant: 'text' as const,
                      color: 'error' as const,
                      onClick: handleDelete,
                      disabled: isLoading,
                    },
                  ]
                : []),
              {
                text: 'Ignore',
                variant: 'text' as const,
                color: 'error' as const,
                onClick: handleReject,
                disabled: isLoading,
              },
            ]
          : canDeleteProcessedEdiDraft
            ? [
                {
                  text: 'Delete',
                  variant: 'text' as const,
                  color: 'error' as const,
                  onClick: handleDelete,
                  disabled: isLoading,
                },
              ]
            : undefined
      }
    >
      <MDBox sx={{ height: '100%', overflow: 'hidden' }}>
        {salesOrderDraft ? (
          <Grid
            container
            spacing={2}
            className="grid-rows-1 h-full overflow-hidden"
          >
            {/* Left side - PO Info & Line Items. This column does NOT scroll:
             *  the header block stays put and the line-items table below is the
             *  only scroll area, so the table keeps its header in view instead
             *  of the whole column sliding under it. */}
            <Grid
              item
              xs={12}
              md={
                hasPendingChanges
                  ? isRightPanelVisible
                    ? 5
                    : 9
                  : isRightPanelVisible
                    ? isActivityLogEnabled
                      ? 7
                      : 6
                    : undefined
              }
              className="h-full flex flex-col"
            >
              <MDBox mb={2} sx={{ flexShrink: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '140px 1fr',
                        gap: 1,
                        '& .label': {
                          fontWeight: 'bold',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <MDTypography className="label">
                          <T context="sales order draft field label">
                            Customer PO #:
                          </T>
                        </MDTypography>
                        <InfoHint contentClassName="max-w-xs">
                          <p>
                            <T>
                              The purchase order number provided by the
                              customer. This helps match incoming orders to
                              customer records and invoices.
                            </T>
                          </p>
                        </InfoHint>
                      </div>
                      <div className="flex items-center justify-between">
                        {isModalEditable ? (
                          isEditingPurchaseOrderNumber ? (
                            <Input
                              className="min-w-[120px] h-7 text-sm px-2 py-1"
                              value={purchaseOrderNumberInput}
                              onChange={(e) =>
                                setPurchaseOrderNumberInput(e.target.value)
                              }
                              onBlur={() => {
                                const trimmed = purchaseOrderNumberInput.trim();
                                if (
                                  trimmed &&
                                  trimmed !==
                                    salesOrderDraft?.purchaseOrderNumber
                                ) {
                                  updateField.mutate({
                                    field: 'purchaseOrderNumber',
                                    value: trimmed,
                                  });
                                }
                                setIsEditingPurchaseOrderNumber(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const trimmed =
                                    purchaseOrderNumberInput.trim();
                                  if (trimmed) {
                                    updateField.mutate({
                                      field: 'purchaseOrderNumber',
                                      value: trimmed,
                                    });
                                  }
                                  setIsEditingPurchaseOrderNumber(false);
                                } else if (e.key === 'Escape') {
                                  setIsEditingPurchaseOrderNumber(false);
                                }
                              }}
                              autoFocus
                            />
                          ) : salesOrderDraft.purchaseOrderNumber ? (
                            <MDTypography
                              variant="body2"
                              sx={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                setIsEditingPurchaseOrderNumber(true)
                              }
                            >
                              {salesOrderDraft.purchaseOrderNumber}
                            </MDTypography>
                          ) : (
                            <MDBox
                              onClick={() =>
                                setIsEditingPurchaseOrderNumber(true)
                              }
                              sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <WarningIcon
                                size={20}
                                className="text-error-main"
                              />
                              <MDTypography variant="caption" color="error">
                                <T context="warning when PO number is missing">
                                  Missing PO Number
                                </T>
                              </MDTypography>
                            </MDBox>
                          )
                        ) : salesOrderDraft.purchaseOrderNumber ? (
                          <MDTypography variant="body2">
                            {salesOrderDraft.purchaseOrderNumber}
                          </MDTypography>
                        ) : (
                          <MDBox
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <WarningIcon
                              size={20}
                              className="text-error-main"
                            />
                            <MDTypography variant="caption" color="error">
                              <T context="warning when PO number is missing">
                                Missing PO Number
                              </T>
                            </MDTypography>
                          </MDBox>
                        )}
                      </div>

                      <MDTypography className="label">
                        <T context="sales order draft field label">Source:</T>
                      </MDTypography>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0, flexWrap: 'wrap' }}
                      >
                        <SourceBadge
                          source={salesOrderDraft.source}
                          onClick={handleToggleRightPanel}
                        />
                        {salesOrderDraft.source === SalesOrderSource.Email &&
                          salesOrderDraft.sourceData?.document
                            ?.originalFileName && (
                            <MDTypography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                minWidth: 0,
                                maxWidth: { xs: '100%', sm: 280 },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={
                                salesOrderDraft.sourceData.document
                                  .originalFileName
                              }
                            >
                              <T context="sales order draft source row, OCR file after email badge">
                                File:{' '}
                                <Var>
                                  {
                                    salesOrderDraft.sourceData.document
                                      .originalFileName
                                  }
                                </Var>
                              </T>
                            </MDTypography>
                          )}
                        {showReextractButton &&
                          isEmailSource &&
                          emailData.inboundEmailId && (
                            <RobotRetryButton
                              onClick={() => setShowReextractDialog(true)}
                              tooltip={gt('Re-extract Sales Order Draft', {
                                context: 'tooltip for re-extract button',
                              })}
                            >
                              <T context="button to re-extract a sales order draft">
                                Re-extract
                              </T>
                            </RobotRetryButton>
                          )}
                      </MDBox>

                      <MDBox className="label">
                        <T context="sales order draft field label">Customer:</T>
                      </MDBox>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <MDBox sx={{ flex: 1, minWidth: 0 }}>
                          {isModalEditable ? (
                            <CustomersAutocomplete
                              selectedCustomerId={salesOrderDraft.customer?.id}
                              onCustomerSelect={handleCustomerSelect}
                              allowClear={false}
                              className="w-full"
                              disabled={updateCustomer.isLoading}
                            />
                          ) : salesOrderDraft.customer ? (
                            <CustomerPill
                              customer={{
                                name:
                                  salesOrderDraft.customer.erpDisplayName ??
                                  salesOrderDraft.customer.name,
                                emailDomain:
                                  salesOrderDraft.customer.emailDomain,
                                phoneNumber:
                                  salesOrderDraft.customer.phoneNumber,
                              }}
                            />
                          ) : (
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              <T>No customer assigned</T>
                            </MDTypography>
                          )}
                        </MDBox>
                        {updateCustomer.isLoading && <Spinner size="sm" />}
                        {customerId && (
                          <MemoryBankNotes
                            staticMetadata={{
                              // workflow: AIWorkflow.EMAIL_SO_INGESTION,
                              workflowStep: 'EXTRACT_SALES_ORDER',
                            }}
                            dynamicMetadata={{
                              customerEmailDomain,
                            }}
                            entityId={customerId}
                            entityType={SystemEntity.AR_CUSTOMER}
                            onNotesChange={refetchSalesOrderDraft}
                            tooltipText="Add notes to guide the AI when processing orders from this customer"
                          />
                        )}
                      </MDBox>

                      <MDBox className="label">
                        <T context="sales order draft field label">
                          Ship To Location:
                        </T>
                      </MDBox>
                      {customerId ? (
                        isModalEditable ? (
                          <LocationPicker
                            customerId={customerId}
                            selectedLocationId={
                              salesOrderDraft.shipToLocationId
                            }
                            onLocationSelect={handleLocationSelect}
                            error={validationErrors.shippingLocation}
                            canShipTo
                          />
                        ) : salesOrderDraft.shipToLocationId ? (
                          <LocationPill
                            locationId={salesOrderDraft.shipToLocationId}
                            customerId={customerId}
                          />
                        ) : (
                          <MDTypography variant="body2" color="text.secondary">
                            <T>No location assigned</T>
                          </MDTypography>
                        )
                      ) : (
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDTypography variant="body2">
                            <T>Please select a customer</T>
                          </MDTypography>
                        </MDBox>
                      )}

                      {/* <MDTypography className="label">
                        Credit Limit:
                      </MDTypography>
                      {salesOrderDraft.customer ? (
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          width="100%"
                          maxWidth="300px"
                        >
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                          >
                            <MDTypography variant="caption">
                              $
                              {(
                                15000 +
                                parseFloat(salesOrderDraft.totalAmount || '0')
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </MDTypography>
                            <MDTypography variant="caption">
                              $50,000.00
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            sx={{
                              width: '100%',
                              height: '6px',
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '3px',
                              overflow: 'hidden',
                            }}
                          >
                            <MDBox
                              sx={{
                                width: `${Math.min(
                                  ((15000 +
                                    parseFloat(
                                      salesOrderDraft.totalAmount || '0',
                                    )) /
                                    50000) *
                                    100,
                                  100,
                                )}%`,
                                height: '100%',
                                backgroundColor: 'success.main',
                                borderRadius: '3px',
                              }}
                            />
                          </MDBox>
                          <MDBox
                            display="flex"
                            justifyContent="flex-start"
                            mt={0.5}
                          >
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Current Balance: $15,000.00
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      ) : (
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: 14 }}
                        >
                          Not available until customer is assigned
                        </MDTypography>
                      )} */}

                      <MDTypography className="label">
                        <T context="sales order draft field label">
                          Ship Date:
                        </T>
                      </MDTypography>
                      {isModalEditable ? (
                        <DatePicker
                          className={cn(
                            salesOrderDraft.shipDate &&
                              isDatePast(salesOrderDraft.shipDate, true) &&
                              'border-red-500',
                          )}
                          placeholder={gt('Set a ship date', {
                            context: 'ship date input placeholder',
                          })}
                          value={parseDateOnly(salesOrderDraft.shipDate)}
                          onSelect={handleInlineShipDateUpdate}
                          disabled={{
                            before: (() => {
                              const t = new Date();
                              t.setHours(0, 0, 0, 0);
                              return t;
                            })(),
                            after: parseDateOnly(salesOrderDraft.deliveryDate),
                          }}
                        />
                      ) : salesOrderDraft.shipDate ? (
                        <MDTypography variant="body2">
                          {parseDateOnly(
                            salesOrderDraft.shipDate,
                          )?.toLocaleDateString()}
                        </MDTypography>
                      ) : (
                        <MDBox>
                          <MDTypography variant="body2">-</MDTypography>
                        </MDBox>
                      )}

                      <MDTypography className="label">
                        <T context="sales order draft field label">
                          Delivery Date:
                        </T>
                      </MDTypography>
                      {isModalEditable ? (
                        <DatePicker
                          className={cn(
                            salesOrderDraft.deliveryDate &&
                              isDatePast(salesOrderDraft.deliveryDate, true) &&
                              'border-red-500',
                          )}
                          placeholder={gt('Set a delivery date', {
                            context: 'delivery date input placeholder',
                          })}
                          value={parseDateOnly(salesOrderDraft.deliveryDate)}
                          onSelect={handleInlineDeliveryDateUpdate}
                          disabled={{
                            before: (() => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const ship = parseDateOnly(
                                salesOrderDraft.shipDate,
                              );
                              return ship && ship > today ? ship : today;
                            })(),
                          }}
                        />
                      ) : salesOrderDraft.deliveryDate ? (
                        <MDTypography variant="body2">
                          {parseDateOnly(
                            salesOrderDraft.deliveryDate,
                          )?.toLocaleDateString()}
                        </MDTypography>
                      ) : (
                        <MDBox>
                          <MDTypography variant="body2">-</MDTypography>
                        </MDBox>
                      )}

                      <MDTypography className="label">
                        <T context="sales order draft field label">Total:</T>
                      </MDTypography>
                      <MDBox display="flex" flexDirection="column">
                        <MDTypography variant="body2">
                          {salesOrderDraft.totalAmount &&
                          !isNaN(parseFloat(salesOrderDraft.totalAmount))
                            ? `$${parseFloat(
                                salesOrderDraft.totalAmount,
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </MDTypography>
                        {salesOrderDraft.extractedTotalAmount &&
                          salesOrderDraft.totalAmount &&
                          parseFloat(
                            String(salesOrderDraft.extractedTotalAmount),
                          ) !==
                            parseFloat(String(salesOrderDraft.totalAmount)) && (
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic', mt: 0.25 }}
                            >
                              <T context="note shown when extracted total differs from calculated total">
                                Note: We initially extracted a total amount of $
                                <Var>
                                  {parseFloat(
                                    String(
                                      salesOrderDraft.extractedTotalAmount,
                                    ),
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Var>
                              </T>
                            </MDTypography>
                          )}
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Line items. The 850 notes banner used to sit here; it now
                  renders under Party Information in the EDI viewer on the
                  right, where the partner's instructions sit beside the
                  parties that sent them. */}
              <ErpSyncErrorBanner
                status={salesOrderDraft?.status}
                processingErrors={salesOrderDraft?.processingErrors}
              />
              <TranslationRefreshBanner
                visible={isModalEditable && isEdiSource}
                customerId={customerId}
                unmatchedLineItemCount={unmatchedLineItemCount}
                salesOrderDraftId={salesOrderDraftId}
                onRefreshed={refetchSalesOrderDraft}
              />
              <MDBox
                // Takes every pixel the header block leaves and scrolls
                // internally. minHeight:0 is what lets a flex child actually
                // shrink to its container instead of overflowing it.
                sx={{ flex: 1, minHeight: 0 }}
                mb={showChargeModal ? 2 : 0}
              >
                <LineItemsTable
                  lineItems={displayLineItems}
                  editable={isModalEditable}
                  isLoading={isLoading}
                  onDeleteLineItem={handleDeleteLineItem}
                  onAddLineItem={handleAddLineItem}
                  onUpdateLineItem={handleUpdateLineItem}
                  onToggleRejectLineItem={handleToggleRejectLineItem}
                  pendingUpdates={pendingUpdates}
                  onSelectLineItem={
                    isAssociateProductV2Enabled
                      ? undefined
                      : (index) => {
                          setSelectedLineItemIndex(index);
                          // Capture a snapshot of the line item at the time the modal opens
                          setSelectedLineItemSnapshot({
                            ...lineItems[index],
                            ...optimisticUpdates[index],
                          });
                        }
                  }
                  customerId={customerId}
                  onAssociateVariantSelected={handleAssociateVariantSelected}
                  isEdiSource={isEdiSource}
                />
              </MDBox>

              {/* Order-level charges */}
              {showChargeModal ? (
                <MDBox mb={2}>
                  <ChargesSection
                    charges={salesOrderDraft.charges || []}
                    onAddCharge={handleAddOrderCharge}
                    onUpdateCharge={handleUpdateOrderCharge}
                    onDeleteCharge={handleDeleteOrderCharge}
                    editable={isModalEditable}
                    isLoading={
                      addOrderCharge.isLoading ||
                      updateOrderCharge.isLoading ||
                      deleteOrderCharge.isLoading
                    }
                    subtotal={displayLineItems.reduce((sum, item) => {
                      if (item.quantity && item.unitPrice) {
                        return sum + item.quantity * item.unitPrice;
                      }
                      return sum + (item.amount || 0);
                    }, 0)}
                    title={gt('Order Charges', {
                      context: 'section title for order-level charges',
                    })}
                    emptyStateText={gt('No order-level charges added', {
                      context:
                        'empty state text when there are no order charges',
                    })}
                  />
                </MDBox>
              ) : (salesOrderDraft.charges?.length ?? 0) > 0 ? (
                // Read-only compact chip row: charges synthesized by the
                // 850 charge translation matcher (e.g. Safeway temperature
                // recorders) ride into Famous as C1 FAPI records. Rendered
                // as wrapping chips so multiple charges stack onto
                // additional rows without crowding the line-items grid.
                // Full edit UI is gated off until the add/update/delete
                // path learns to preserve the underlying famousChargeIdx
                // linkage.
                <MDBox
                  mt={1}
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <MDTypography variant="caption" fontWeight="medium">
                    <T context="section label for order-level charges list">
                      Order Charges:
                    </T>
                  </MDTypography>
                  {(salesOrderDraft.charges ?? []).map((charge) => (
                    <Badge key={charge.id} variant="outline">
                      {`${charge.description} — $${Number(
                        charge.amount,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </Badge>
                  ))}
                </MDBox>
              ) : null}
            </Grid>

            {/* Middle - Activity Graph / Source Document Viewer */}
            {isRightPanelVisible && (
              <Grid
                item
                xs={12}
                md={hasPendingChanges ? 4 : isActivityLogEnabled ? 5 : 6}
                className="h-full flex flex-col min-h-0"
              >
                {isActivityLogEnabled ? (
                  <ActivityGraph
                    entityType={SystemEntity.AR_STAGED_SALES_ORDER}
                    entityId={salesOrderDraftId}
                    conversationId={
                      salesOrderDraft?.sourceEmail?.conversationId
                    }
                    className="h-full min-h-0"
                  />
                ) : (
                  <SourceDocumentViewer
                    sourceFlags={sourceFlags}
                    emailData={emailData}
                    conversationId={
                      salesOrderDraft?.sourceEmail?.conversationId
                    }
                    ordefulMessage={ediData.ordefulMessage}
                    document={documentData.document}
                    orderful850Payload={salesOrderDraft?.orderful850Payload}
                    orderful860Payloads={salesOrderDraft?.orderful860Payloads}
                    ediNotes={salesOrderDraft?.ediNotes}
                    onRefreshData={() => refetchSalesOrderDraft()}
                  />
                )}
              </Grid>
            )}

            {/* Right side - Pending 860 Changes */}
            {hasPendingChanges && (
              <Grid item xs={12} md={3} className="h-full overflow-y-auto">
                <PendingChangesPanel
                  key={pendingChangesKey}
                  salesOrderDraftId={salesOrderDraftId}
                  pendingLineItemChanges={pendingLineItemChanges}
                  pendingHeaderChanges={pendingHeaderChanges}
                  originalTotalAmount={
                    salesOrderDraft?.totalAmount != null
                      ? Number(salesOrderDraft.totalAmount)
                      : null
                  }
                  onReviewComplete={() => {
                    refetchSalesOrderDraft();
                    onUpdate?.();
                  }}
                />
              </Grid>
            )}
          </Grid>
        ) : (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            flexDirection="column"
          >
            {isSalesOrderDraftError ? (
              <>
                <MDTypography variant="h6" color="error" mb={1}>
                  <T>Sales order not found</T>
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <T>
                    The sales order draft could not be loaded. It may have been
                    deleted or the link may be incorrect.
                  </T>
                </MDTypography>
              </>
            ) : null}
          </MDBox>
        )}

        {renderAssociateModal()}

        {showDeleteLineItemModal && deletingLineItem && (
          <DeleteLineItemModal
            open={showDeleteLineItemModal}
            onClose={() => {
              setShowDeleteLineItemModal(false);
              setDeletingLineItem(undefined);
              setDeletingLineItemIndex(undefined);
            }}
            onConfirm={handleConfirmDelete}
            lineItem={deletingLineItem}
            isLoading={deleteLineItem.isLoading}
            error={deleteLineItem.error || undefined}
          />
        )}

        {/* Delete confirmation dialog */}
        <Dialog
          open={showDeleteModal}
          onOpenChange={(v) => {
            if (!v) setShowDeleteModal(false);
          }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                <T context="confirmation dialog title">
                  Delete Sales Order Draft
                </T>
              </DialogTitle>
            </DialogHeader>
            <MDTypography variant="body2">
              <T>
                Are you sure you want to permanently delete this sales order
                draft? This action cannot be undone.
              </T>
            </MDTypography>
            {salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED && (
              <MDTypography variant="body2" color="error">
                <T context="warning when deleting an already-processed EDI draft">
                  This order was already processed. Deleting the draft does not
                  cancel the order in Famous, and the sales order created from
                  it will remain — only its link back to this draft is removed.
                </T>
              </MDTypography>
            )}
            <DialogFooter>
              <MDButton
                variant="text"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteStagedPurchaseOrder.isLoading}
              >
                <T context="cancel button label">Cancel</T>
              </MDButton>
              <MDButton
                variant="contained"
                color="error"
                onClick={handleConfirmDeleteDraft}
                disabled={deleteStagedPurchaseOrder.isLoading}
              >
                {deleteStagedPurchaseOrder.isLoading ? (
                  <T context="delete button loading state">Deleting...</T>
                ) : (
                  <T context="delete button label">Delete</T>
                )}
              </MDButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {showRejectModal && (
          <RejectSalesOrderDraftModal
            open={showRejectModal}
            onClose={() => setShowRejectModal(false)}
            onConfirm={handleConfirmReject}
            isLoading={rejectStagedPurchaseOrder.isLoading}
            error={rejectStagedPurchaseOrder.error || undefined}
          />
        )}

        {/* Re-extract Sales Order Draft Dialog */}
        {showReextractDialog &&
          emailData.inboundEmailId &&
          salesOrderDraft?.id && (
            <ReextractSalesOrderDraftDialog
              inboundEmailId={emailData.inboundEmailId}
              classificationId={
                salesOrderDraft?.sourceEmail?.classification
                  ?.classificationResult
              }
              open={showReextractDialog}
              onOpenChange={setShowReextractDialog}
              metadata={{
                customerEmailDomain,
              }}
              entityId={salesOrderDraft.id}
              entityType={SystemEntity.AR_STAGED_SALES_ORDER}
              onSuccess={() => {
                // Refresh the sales order draft data when reextraction succeeds
                refetchSalesOrderDraft();
                onUpdate?.();
                showToast({
                  title: 'Re-extraction Complete',
                  message:
                    'Sales order draft has been re-extracted successfully.',
                  color: 'success',
                  icon: 'check_circle',
                });
              }}
            />
          )}
      </MDBox>
      <SyncErrorDialog
        syncErrorState={{
          isOpen: syncErrorDialogOpen,
          message: syncErrorMessage,
          erpSystemName: erpSystemName,
        }}
        onClose={() => {
          setSyncErrorDialogOpen(false);
          setSyncErrorMessage(null);
          setErpSystemName(null);
        }}
      />
    </Modal>
  );
};

export default SalesOrderDraftModal;
